import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@wammasys/supabase';
import { toast } from 'react-hot-toast';

interface Restaurant {
  id: string;
  name: string;
  owner_id: string;
  logo_url?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

interface RestaurantContextType {
  restaurant: Restaurant | null;
  isLoading: boolean;
  error: string | null;
  refetchRestaurant: () => Promise<void>;
}

const RestaurantContext = createContext<RestaurantContextType>({
  restaurant: null,
  isLoading: false,
  error: null,
  refetchRestaurant: async () => {},
});

export const useRestaurantContext = () => useContext(RestaurantContext);

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRestaurant = async () => {
    if (!user) {
      console.log('No user found in RestaurantContext, setting restaurant to null');
      setRestaurant(null);
      return;
    }

    console.log('Fetching restaurant for user:', user.id);
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('owner_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No restaurant found for this user - not an error
          console.log('No restaurant found for user:', user.id);
          setRestaurant(null);
        } else {
          console.error('Error fetching restaurant:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          throw error;
        }
      } else {
        console.log('Restaurant found:', data);
        setRestaurant(data);
      }
    } catch (err: any) {
      console.error('Error fetching restaurant:', err);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to fetch restaurant details');
      toast.error('Could not load restaurant details');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch restaurant when user changes
  useEffect(() => {
    fetchRestaurant();
  }, [user]);

  return (
    <RestaurantContext.Provider
      value={{
        restaurant,
        isLoading,
        error,
        refetchRestaurant: fetchRestaurant,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
}; 
